import React, { useState, useEffect } from 'react';
import { Clock, CalendarIcon, Link as LinkIcon, FileText, User } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useForm } from 'react-hook-form';
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from '@/contexts/AuthContext';
import { toast } from "@/components/ui/use-toast";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DatePicker } from "@/components/DatePicker";
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription
} from "@/components/ui/form";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";

import { TimeInput, WorkLogFormValues } from '@/types/worklog';

interface ClientUser {
  email: string;
  organization_name: string;
  horas_mes_contratadas?: number;
  horas_mes_consumidas?: number;
  access?: string[]; // Optional access property
}

interface WorkLogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const WorkLogDialog: React.FC<WorkLogDialogProps> = ({ open, onOpenChange, onSuccess }) => {
  const [clients, setClients] = useState<ClientUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientUser | null>(null);
  const [workDate, setWorkDate] = useState<Date>(new Date());
  const [showMeetingType, setShowMeetingType] = useState(false);
  const { user } = useAuth();

  const form = useForm<WorkLogFormValues>({
    resolver: zodResolver(z.object({
      workDate: z.date({ required_error: "La fecha es requerida" }),
      clientEmail: z.string().min(1, "El cliente es requerido"),
      timeInput: z.object({
        hours: z.number().min(0),
        minutes: z.number().min(0).max(59)
      }).refine(data => (data.hours > 0 || data.minutes > 0), {
        message: "Debe especificar al menos 1 minuto",
      }),
      workType: z.enum(["Reunión", "Trabajo Técnico", "Soporte"]),
      meetingType: z.enum(["Pagada", "Sin Costo"]).optional(),
      meetingLink: z.string().url("El link debe ser una URL válida").min(1, "El link es requerido"),
      input_type: z.enum(["description", "transcription", ""]),
      summary: z.string().optional(),
      transcripcion: z.string().optional(),
    }).superRefine((data, ctx) => {
      if (data.input_type === "description" && !data.summary) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "La descripción es requerida",
          path: ["summary"]
        });
      }
      if (data.input_type === "transcription" && !data.transcripcion) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "La transcripción es requerida",
          path: ["transcripcion"]
        });
      }
      if (showMeetingType && !data.meetingType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "El tipo de reunión es requerido",
          path: ["meetingType"]
        });
      }
    })),
    defaultValues: {
      workDate: new Date(),
      clientEmail: "",
      timeInput: { hours: 0, minutes: 0 },
      workType: "Reunión",
      meetingLink: "",
      input_type: "",
      summary: "",
      transcripcion: "",
    }
  });

  useEffect(() => {
    if (open) {
      form.reset({
        workDate: new Date(),
        clientEmail: "",
        timeInput: { hours: 0, minutes: 0 },
        workType: "Reunión",
        meetingLink: "",
        input_type: "",
        summary: "",
        transcripcion: "",
      });
      setSelectedClient(null);
      setWorkDate(new Date());
      setShowMeetingType(false);
      fetchClients();
    }
  }, [open]);

  const fetchClients = async () => {
    try {
      const { data: principalUsers, error: principalError } = await supabase
        .from('profiles')
        .select('email, organization_name, horas_mes_contratadas, horas_mes_consumidas, access')
        .in('role', ['Usuario Principal', 'Administrador']);
      
      if (principalError) throw principalError;
      setClients(principalUsers || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudieron cargar los clientes.",
      });
    }
  };

  const onClientChange = async (clientEmail: string) => {
    const client = clients.find(c => c.email === clientEmail);
    setSelectedClient(client || null);
    form.setValue('clientEmail', clientEmail);
    
    if (client?.access?.includes('Soporte Hubspot (MKT/Sales Ops)')) {
      setShowMeetingType(true);
    } else {
      setShowMeetingType(false);
      form.setValue('meetingType', undefined);
    }
  };

  const handleSubmit = async (values: WorkLogFormValues) => {
    if (!user?.email) return;
    
    setLoading(true);
    
    try {
      const totalMinutes = (values.timeInput.hours * 60) + values.timeInput.minutes;
      
      const workLogData = {
        created_by_email: user.email,
        client_email: values.clientEmail,
        client_organization: selectedClient?.organization_name,
        work_date: format(values.workDate, 'yyyy-MM-dd'),
        minutes: totalMinutes,
        work_type: values.workType,
        summary: values.input_type === 'description' ? values.summary : '',
        transcripcion: values.input_type === 'transcription' ? values.transcripcion : '',
        meeting_link: values.meetingLink,
        meeting_type: values.meetingType
      };
      
      const { data: inserted, error } = await supabase
        .from('work_logs')
        .insert(workLogData)
        .select('id')
        .single();
      
      if (error) throw error;
      
      toast({
        title: "Éxito",
        description: "Horas de trabajo registradas correctamente.",
      });
      
      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }

      // Fire and forget: invoke edge function to generate embeddings for this log
      if (inserted?.id) {
        supabase.functions.invoke('ingest_work_log_chunks', {
          body: { workLogId: inserted.id }
        }).catch((fnErr) => {
          console.error('Error invoking ingest_work_log_chunks:', fnErr);
        });
      }
    } catch (error) {
      console.error('Error registering work hours:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudieron registrar las horas de trabajo.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-[600px] max-h-[90vh] overflow-hidden no-transparency" 
        style={{ backgroundColor: "white" }}
      >
        <DialogHeader>
          <DialogTitle>Registrar Trabajo</DialogTitle>
          <DialogDescription>Registra tus horas de trabajo para los clientes.</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="overflow-y-auto pr-4 max-h-[calc(80vh-180px)]">
              <div className="space-y-4 pb-4">
                <FormField
                  control={form.control}
                  name="workDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Fecha de trabajo <span className="text-red-500">*</span></FormLabel>
                      <DatePicker
                        date={workDate}
                        onDateChange={(date) => {
                          if (date) {
                            date.setHours(9, 0, 0, 0);
                            setWorkDate(date);
                            field.onChange(date);
                          }
                        }}
                        placeholder="Selecciona fecha"
                        allowPastDates={true}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="clientEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cliente <span className="text-red-500">*</span></FormLabel>
                      <Select onValueChange={onClientChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <User className="h-4 w-4 mr-2" />
                            <SelectValue placeholder="Selecciona un cliente" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent position="popper" className="max-h-[200px]">
                          {clients.map((client) => (
                            <SelectItem key={client.email} value={client.email}>
                              {client.email} - {client.organization_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="timeInput.hours"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Horas <span className="text-red-500">*</span></FormLabel>
                        <FormControl>
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-2" />
                            <Input 
                              type="number"
                              min="0"
                              {...field}
                              onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="timeInput.minutes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Minutos <span className="text-red-500">*</span></FormLabel>
                        <FormControl>
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-2" />
                            <Input 
                              type="number"
                              min="0"
                              max="59"
                              {...field}
                              onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <FormField
                  control={form.control}
                  name="workType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de uso <span className="text-red-500">*</span></FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <FileText className="h-4 w-4 mr-2" />
                            <SelectValue placeholder="Selecciona un tipo" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent position="popper">
                          <SelectItem value="Reunión">Reunión</SelectItem>
                          <SelectItem value="Trabajo Técnico">Trabajo Técnico</SelectItem>
                          <SelectItem value="Soporte">Soporte</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                {showMeetingType && (
                  <FormField
                    control={form.control}
                    name="meetingType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tipo de Reunión <span className="text-red-500">*</span></FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          value={field.value || ""}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecciona un tipo" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent position="popper">
                            <SelectItem value="Pagada">Pagada</SelectItem>
                            <SelectItem value="Sin Costo">Sin Costo</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                
                <FormField
                  control={form.control}
                  name="meetingLink"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Link de reunión o video <span className="text-red-500">*</span></FormLabel>
                      <FormControl>
                        <div className="flex">
                          <LinkIcon className="h-4 w-4 mr-2 mt-3 text-gray-400" />
                          <Input 
                            type="url" 
                            placeholder="https://meet.google.com/... o https://www.loom.com/..." 
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="input_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de entrada <span className="text-red-500">*</span></FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar tipo de entrada" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent position="popper">
                          <SelectItem value="transcription">Transcripción</SelectItem>
                          <SelectItem value="description">Descripción</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                {form.watch('input_type') === 'description' && (
                  <FormField
                    control={form.control}
                    name="summary"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Descripción <span className="text-red-500">*</span></FormLabel>
                        <FormControl>
                          <RichTextEditor
                            value={field.value || ""}
                            onChange={field.onChange}
                            dir="ltr"
                            className="min-h-[200px]"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {form.watch('input_type') === 'transcription' && (
                  <FormField
                    control={form.control}
                    name="transcripcion"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Transcripción <span className="text-red-500">*</span></FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            rows={10}
                            placeholder="Pega aquí la transcripción de la reunión..."
                            className="resize-none"
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </div>
            
            <DialogFooter className="pt-4 border-t mt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit"
                disabled={loading}
                className="bg-[#4C8EF1] text-white hover:bg-[#4C8EF1]/90"
              >
                {loading ? "Procesando..." : "Registrar hora"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default WorkLogDialog;
